import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionTitle, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDT } from "@/lib/format";
import { Plus, Wrench } from "lucide-react";
import { toast } from "sonner";

const MACHINE_STATUSES = ["running", "idle", "breakdown", "maintenance", "setup", "inactive"];
const DT_REASONS = ["Machine Breakdown", "Power Failure", "Material Shortage", "Quality Issue", "Changeover", "Setup", "Manpower Shortage", "Maintenance", "Other"];
const EXC_TYPES = ["material_shortage", "machine_breakdown", "production_delay", "quality_failure", "excess_rejection", "excess_wastage", "manpower_shortage", "process_failure", "packing_issue", "urgent_order"];
const STAGE_LABEL = { production: "Production", processing: "Processing", frosting: "Frosting", decoration: "Decoration/Print", qc: "QC", packing: "Packing" };

const stageChip = (s) => s.status === "completed" ? "bg-signal-green/15 text-signal-green" :
  s.status === "qc_failed" ? "bg-signal-red/15 text-signal-red" :
  s.status === "task_created" ? "bg-signal-blue/15 text-signal-blue" : "bg-muted text-muted-foreground";

export default function Factory() {
  const { user } = useAuth();
  const [tab, setTab] = useState("pipeline");
  const [dash, setDash] = useState(null);
  const [machines, setMachines] = useState([]);
  const [downtime, setDowntime] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [mOpen, setMOpen] = useState(false);
  const [mForm, setMForm] = useState({ name: "", department: "Factory / Manufacturing", process: "production", capacity_per_hour: "" });
  const [dtOpen, setDtOpen] = useState(false);
  const [dtForm, setDtForm] = useState({ machine_id: "", reason: "Machine Breakdown", remarks: "" });
  const [exOpen, setExOpen] = useState(false);
  const [exForm, setExForm] = useState({ type: "material_shortage", description: "", priority: "medium", mo_id: "" });
  const [resolveTarget, setResolveTarget] = useState(null);
  const [resolveNote, setResolveNote] = useState("");
  const canManage = ["admin", "factory_head", "plant_manager", "production", "production_supervisor", "shift_incharge", "maintenance_head", "engineer"].includes(user?.role);

  const load = useCallback(async () => {
    try {
      const [d, m, dt, ex] = await Promise.all([
        api.get("/factory/dashboard"), api.get("/factory/machines"),
        api.get("/factory/downtime"), api.get("/factory/exceptions"),
      ]);
      setDash(d.data); setMachines(m.data); setDowntime(dt.data); setExceptions(ex.data);
    } catch (e) {
      if (e.response?.status === 403) toast.error("You do not have factory access");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    try { await fn(); toast.success(ok); load(); }
    catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed"); }
  };

  return (
    <div className="space-y-5" data-testid="factory-page">
      <SectionTitle right={canManage && (
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setExOpen(true)} data-testid="raise-exception-button">Raise Exception</Button>
          <Button variant="outline" onClick={() => setDtOpen(true)} data-testid="record-downtime-button">Record Downtime</Button>
          <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => setMOpen(true)} data-testid="new-machine-button"><Plus className="w-4 h-4 mr-1" /> Machine</Button>
        </div>
      )}>Factory / Manufacturing Control</SectionTitle>

      {dash && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="factory-kpis">
          {[["open_stage_tasks", "Open Stage Tasks"], ["active_mos", "Active MOs", dash.active_mos.length],
            ["total_machines", "Machines"], ["open_downtime", "Open Downtime", null, dash.open_downtime > 0],
            ["open_exceptions", "Open Exceptions", null, dash.open_exceptions > 0]].map(([k, label, override, alert]) => (
            <div key={k} className={`bg-card border rounded-lg p-3 ${alert ? "border-signal-red/50" : "border-border"}`} data-testid={`factory-kpi-${k}`}>
              <p className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">{label}</p>
              <p className={`metric text-lg font-extrabold mt-0.5 ${alert ? "text-signal-red" : ""}`}>{override ?? dash[k] ?? 0}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-1 border-b border-border" data-testid="factory-tabs">
        {[["pipeline", "MO Pipeline"], ["machines", "Machines"], ["downtime", "Downtime"], ["exceptions", "Exceptions"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`factory-tab-${k}`}
            className={`px-4 py-2 text-xs font-mono uppercase tracking-widest border-b-2 transition-colors ${tab === k ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>{l}</button>
        ))}
      </div>

      {tab === "pipeline" && dash && (
        <div className="space-y-2" data-testid="mo-pipeline">
          {dash.active_mos.map((m) => (
            <div key={m.mo_id} className="bg-card border border-border rounded-lg p-3 flex items-center gap-4 flex-wrap" data-testid={`mo-pipe-${m.mo_id}`}>
              <div className="min-w-36">
                <p className="metric font-bold text-primary">{m.mo_id}</p>
                <p className="text-xs text-muted-foreground">{m.fg_sku} · {m.quantity.toLocaleString()} pcs</p>
              </div>
              <StatusBadge status={m.status} />
              <div className="flex gap-1.5 flex-wrap">
                {(m.stages || []).map((s) => (
                  <span key={s.seq} className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded ${stageChip(s)}`} data-testid={`mo-stage-${m.mo_id}-${s.stage}`}>
                    {STAGE_LABEL[s.stage] || s.stage}{s.task_id ? ` · ${s.task_id}` : ""}
                  </span>
                ))}
              </div>
              {m.stages?.some((s) => s.status === "qc_failed") && canManage && !m.stages?.some((s) => s.stage === "qc" && s.status === "task_created") && (
                <Button size="sm" variant="outline" className="h-7 text-xs border-signal-red/50 text-signal-red"
                  onClick={() => act(() => api.post(`/production/orders/${m.mo_id}/retry-qc`), "QC re-inspection task created")}
                  data-testid={`retry-qc-${m.mo_id}`}>Retry QC</Button>
              )}
            </div>
          ))}
          {dash.active_mos.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">No active production orders — create one from the Productions page.</p>}
        </div>
      )}

      {tab === "machines" && (
        <div className="bg-card border border-border rounded-lg overflow-x-auto">
          <table className="w-full text-sm" data-testid="machines-table">
            <thead><tr className="border-b border-border text-left">
              {["Machine", "Name", "Process", "Department", "Cap/hr", "Status", ""].map((h) => <th key={h} className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{h}</th>)}
            </tr></thead>
            <tbody>
              {machines.map((m) => (
                <tr key={m.machine_id} className="border-b border-border/50" data-testid={`machine-row-${m.machine_id}`}>
                  <td className="px-4 py-3 metric font-bold text-primary">{m.machine_id}</td>
                  <td className="px-4 py-3">{m.name}</td>
                  <td className="px-4 py-3 text-xs uppercase font-mono">{m.process || "—"}</td>
                  <td className="px-4 py-3 text-xs">{m.department}</td>
                  <td className="px-4 py-3 metric">{m.capacity_per_hour || "—"}</td>
                  <td className="px-4 py-3"><StatusBadge status={m.status} /></td>
                  <td className="px-4 py-3">
                    {canManage && (
                      <Select value={m.status} onValueChange={(v) => act(() => api.patch(`/factory/machines/${m.machine_id}`, { status: v }), `Machine ${v}`)}>
                        <SelectTrigger className="w-32 h-8 text-xs" data-testid={`machine-status-${m.machine_id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{MACHINE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                  </td>
                </tr>
              ))}
              {machines.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No machines registered</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "downtime" && (
        <div className="bg-card border border-border rounded-lg overflow-x-auto">
          <table className="w-full text-sm" data-testid="downtime-table">
            <thead><tr className="border-b border-border text-left">
              {["ID", "Machine", "Reason", "Start", "Duration", "Reported By", "Status", ""].map((h) => <th key={h} className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{h}</th>)}
            </tr></thead>
            <tbody>
              {downtime.map((d) => (
                <tr key={d.downtime_id} className="border-b border-border/50" data-testid={`downtime-row-${d.downtime_id}`}>
                  <td className="px-4 py-3 metric text-xs">{d.downtime_id}</td>
                  <td className="px-4 py-3 text-xs">{d.machine_name} <span className="metric text-muted-foreground">({d.machine_id})</span></td>
                  <td className="px-4 py-3 text-xs">{d.reason}</td>
                  <td className="px-4 py-3 metric text-xs">{fmtDT(d.start_time)}</td>
                  <td className="px-4 py-3 metric">{d.duration_minutes != null ? `${d.duration_minutes}m` : "ongoing"}</td>
                  <td className="px-4 py-3 text-xs">{d.reported_by}</td>
                  <td className="px-4 py-3">{d.end_time ? <span className="text-[10px] font-mono uppercase text-signal-green">resolved</span> : <span className="text-[10px] font-mono uppercase text-signal-red">open</span>}</td>
                  <td className="px-4 py-3">{!d.end_time && canManage && (
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => act(() => api.post(`/factory/downtime/${d.downtime_id}/resolve`), "Downtime resolved")} data-testid={`resolve-dt-${d.downtime_id}`}>Resolve</Button>
                  )}</td>
                </tr>
              ))}
              {downtime.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">No downtime recorded</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "exceptions" && (
        <div className="bg-card border border-border rounded-lg overflow-x-auto">
          <table className="w-full text-sm" data-testid="factory-exceptions-table">
            <thead><tr className="border-b border-border text-left">
              {["ID", "Type", "Priority", "Description", "MO", "Raised By", "Status", ""].map((h) => <th key={h} className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{h}</th>)}
            </tr></thead>
            <tbody>
              {exceptions.map((e) => (
                <tr key={e.exception_id} className="border-b border-border/50" data-testid={`exc-row-${e.exception_id}`}>
                  <td className="px-4 py-3 metric text-xs">{e.exception_id}</td>
                  <td className="px-4 py-3 text-xs uppercase font-mono">{e.type.replace(/_/g, " ")}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] font-mono uppercase ${e.priority === "critical" ? "text-signal-red" : e.priority === "high" ? "text-signal-amber" : "text-muted-foreground"}`}>{e.priority}</span></td>
                  <td className="px-4 py-3 text-xs max-w-64 truncate">{e.reason}</td>
                  <td className="px-4 py-3 metric text-xs">{e.mo_id || "—"}</td>
                  <td className="px-4 py-3 text-xs">{e.raised_by}</td>
                  <td className="px-4 py-3"><StatusBadge status={e.status} /></td>
                  <td className="px-4 py-3">{e.status === "open" && canManage && (
                    <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`resolve-exc-${e.exception_id}`}
                      onClick={() => { setResolveTarget(e); setResolveNote(""); }}>Resolve</Button>
                  )}</td>
                </tr>
              ))}
              {exceptions.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">No factory exceptions</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!resolveTarget} onOpenChange={() => setResolveTarget(null)}>
        <DialogContent className="bg-card border-border" data-testid="resolve-exception-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl uppercase">Resolve Exception — {resolveTarget?.exception_id}</DialogTitle><DialogDescription className="sr-only">Record the resolution for this factory exception</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">{resolveTarget?.type?.replace(/_/g, " ")} — {resolveTarget?.reason}</p>
            <div><Label>Resolution *</Label><Input data-testid="resolve-note" value={resolveNote} onChange={(e) => setResolveNote(e.target.value)} placeholder="What was done to resolve this?" /></div>
            <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground" disabled={!resolveNote.trim()} data-testid="resolve-submit"
              onClick={() => act(async () => { await api.post(`/factory/exceptions/${resolveTarget.exception_id}/resolve`, { resolution: resolveNote }); setResolveTarget(null); }, "Exception resolved")}>Resolve Exception</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={mOpen} onOpenChange={setMOpen}>
        <DialogContent className="bg-card border-border" data-testid="machine-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl uppercase">New Machine</DialogTitle><DialogDescription className="sr-only">Register a machine</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input data-testid="machine-name" value={mForm.name} onChange={(e) => setMForm({ ...mForm, name: e.target.value })} placeholder="ISBM-02" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Process</Label>
                <Select value={mForm.process} onValueChange={(v) => setMForm({ ...mForm, process: v })}>
                  <SelectTrigger data-testid="machine-process"><SelectValue /></SelectTrigger>
                  <SelectContent>{["production", "processing", "frosting", "decoration", "packing"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Capacity / hr</Label><Input type="number" data-testid="machine-cap" value={mForm.capacity_per_hour} onChange={(e) => setMForm({ ...mForm, capacity_per_hour: e.target.value })} /></div>
            </div>
            <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="machine-create-submit"
              onClick={() => act(async () => { await api.post("/factory/machines", { ...mForm, capacity_per_hour: parseInt(mForm.capacity_per_hour) || null }); setMOpen(false); setMForm({ name: "", department: "Factory / Manufacturing", process: "production", capacity_per_hour: "" }); }, "Machine created")}>Create Machine</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dtOpen} onOpenChange={setDtOpen}>
        <DialogContent className="bg-card border-border" data-testid="downtime-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl uppercase">Record Downtime</DialogTitle><DialogDescription className="sr-only">Record machine downtime</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Machine *</Label>
              <Select value={dtForm.machine_id} onValueChange={(v) => setDtForm({ ...dtForm, machine_id: v })}>
                <SelectTrigger data-testid="dt-machine"><SelectValue placeholder="Select machine" /></SelectTrigger>
                <SelectContent>{machines.map((m) => <SelectItem key={m.machine_id} value={m.machine_id}>{m.machine_id} — {m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Reason *</Label>
              <Select value={dtForm.reason} onValueChange={(v) => setDtForm({ ...dtForm, reason: v })}>
                <SelectTrigger data-testid="dt-reason"><SelectValue /></SelectTrigger>
                <SelectContent>{DT_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Remarks</Label><Input data-testid="dt-remarks" value={dtForm.remarks} onChange={(e) => setDtForm({ ...dtForm, remarks: e.target.value })} /></div>
            <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground" disabled={!dtForm.machine_id} data-testid="dt-submit"
              onClick={() => act(async () => { await api.post("/factory/downtime", dtForm); setDtOpen(false); }, "Downtime recorded — machine set to breakdown")}>Record Downtime</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={exOpen} onOpenChange={setExOpen}>
        <DialogContent className="bg-card border-border" data-testid="exception-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl uppercase">Raise Factory Exception</DialogTitle><DialogDescription className="sr-only">Raise a factory exception</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Type</Label>
                <Select value={exForm.type} onValueChange={(v) => setExForm({ ...exForm, type: v })}>
                  <SelectTrigger data-testid="exc-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{EXC_TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Priority</Label>
                <Select value={exForm.priority} onValueChange={(v) => setExForm({ ...exForm, priority: v })}>
                  <SelectTrigger data-testid="exc-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>{["low", "medium", "high", "critical"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>MO (optional)</Label><Input data-testid="exc-mo" value={exForm.mo_id} onChange={(e) => setExForm({ ...exForm, mo_id: e.target.value })} placeholder="MO-00001" className="font-mono" /></div>
            <div><Label>Description *</Label><Input data-testid="exc-desc" value={exForm.description} onChange={(e) => setExForm({ ...exForm, description: e.target.value })} /></div>
            <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground" disabled={!exForm.description} data-testid="exc-submit"
              onClick={() => act(async () => { await api.post("/factory/exceptions", { ...exForm, mo_id: exForm.mo_id || null }); setExOpen(false); setExForm({ type: "material_shortage", description: "", priority: "medium", mo_id: "" }); }, "Exception raised — critical issues notify management")}>Raise Exception</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
